import { useEffect } from 'react';

/**
 * Shared full-screen takeover helpers — the focus/keyboard semantics the reviewer
 * pioneered, extracted so the Workflow Studio reuses them verbatim (one rule, not
 * two). Used by ReviewerInner and AdminSocialWorkflowPage's Studio chrome.
 */

/**
 * A text-entry element: Escape must NEVER fire a global exit/cancel while the
 * manager is typing in one (it would discard their work mid-edit). Mirrors the
 * reviewer's in-place guard exactly.
 */
export const isTextField = el =>
    !!el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable);

/**
 * The reviewer deep-link for a single post — the ONE place this route string
 * lives (the reviewer page, the dashboard, and the Studio's awaiting-exit link
 * all build it here, so the route is changed in one spot).
 *
 * @param {string|number} id the SocialPost id
 * @returns {string} the /admin-social/review?post=ID href
 */
export const reviewPostHref = id => `/admin-social/review?post=${id}`;

/**
 * Bind Escape-to-exit on a full-screen takeover. Never fires while a text field
 * is focused (the isTextField guard), uses capture so it wins over inner handlers,
 * and only binds while `active`. The handler is responsible for whatever "exit"
 * means (history back, close overlay, …).
 *
 * @param {boolean} active whether the binding is live
 * @param {() => void} onExit invoked on a clean Escape
 */
export const useEscapeExit = (active, onExit) => {
    useEffect(() => {
        if (!active || typeof document === 'undefined') return undefined;
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            if (isTextField(document.activeElement)) return;
            e.stopPropagation();
            onExit();
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [active, onExit]);
};
